// packages/utils/src/shared/fetchApiRequest.ts
//
// The browser widgets' HTTP client: `apiRequest`'s Result contract for JSON endpoints, over the platform's `fetch`.
// It exists for the embed bundles' size budget (apps/embed/check-bundle-size.js, docs/embed-seo.md §5): axios was
// ~37 KB of each of `main.js`, `chat.js`, `reviews.js` and `terms.js`, and a widget uses none of what it adds (Node's
// http adapter, mTLS agents, XML bodies, retries, progress events). The server keeps `apiRequest`.
//
// The request is the one `apiRequest` made through axios's browser adapter: axios's `Accept`; `Content-Type` only with
// a JSON body (`application/json` unless the caller names one; none on a request without a body, none on a FormData
// body, whose multipart boundary only the browser can write); cookies to the page's own origin only, unless
// `withCredentials`; the same time limit. Every HTTP status is answered and the body decides (axios ran with
// `validateStatus: () => true`); a JSON body is parsed and any other text kept as it came (axios's lenient parsing).
// The outcomes are `apiRequestOutcome.ts`'s, shared with `apiRequest`.
import type { Result } from '@tourtask/types/result';
import { failure } from '@tourtask/utils/shared/result';
import {
  requestNetworkFailure,
  requestTimeoutFailure,
  resultOfResponseBody,
  type ApiRequestExpect,
} from '@tourtask/utils/shared/apiRequestOutcome';

/** axios's default `Accept`: the API keeps seeing the request it always saw. */
const ACCEPT = 'application/json, text/plain, */*';

export type FetchApiRequestMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface FetchApiRequestParams {
  /** Default `POST`, as `apiRequest`'s. */
  method?: FetchApiRequestMethod;
  url: string;
  /**
   * A plain object is sent as JSON, a `FormData` as `multipart/form-data`. A GET carries none: the widgets put a
   * GET's parameters in its URL, and one given a body is refused rather than sent without it.
   */
  body?: Record<string, unknown> | FormData;
  headers?: Record<string, string>;
  /** Send cookies to another origin too (default true, as `apiRequest`); false sends them to the page's own only. */
  withCredentials?: boolean;
  /** Abort after this long, in ms (default 15000); 0 = no limit. */
  timeoutMs?: number;
  /** Default `raw`, as `apiRequest`'s. */
  expect?: ApiRequestExpect;
  /** The `fetch` to call; tests pass their own. */
  fetchImpl?: typeof fetch;
}

const isFormData = (body: unknown): body is FormData => typeof FormData !== 'undefined' && body instanceof FormData;

/** A response's text as axios handed it over: JSON parsed, any other text (an empty body too) as it came. */
function parseResponseText(text: string): unknown {
  if (text === '') return text;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export async function fetchApiRequest<T = unknown>(params: FetchApiRequestParams): Promise<Result<T>> {
  const {
    method = 'POST',
    url,
    body,
    headers = {},
    withCredentials = true,
    timeoutMs = 15000,
    expect = 'raw',
    fetchImpl = fetch,
  } = params;

  if (method === 'GET' && body !== undefined) {
    return failure({
      kind: 'VALIDATION',
      code: 'HTTP.INVALID_GET_BODY',
      message: 'GET requests carry no body here: put the parameters in the URL',
      details: { url },
    });
  }

  const payload = body === undefined ? undefined : isFormData(body) ? body : JSON.stringify(body);
  // `Headers` matches names without regard to case, so a caller's `accept` replaces axios's default instead of
  // joining it.
  const requestHeaders = new Headers({ Accept: ACCEPT });
  for (const [name, value] of Object.entries(headers)) requestHeaders.set(name, value);
  if (typeof payload !== 'string') requestHeaders.delete('Content-Type');
  else if (!requestHeaders.has('Content-Type')) requestHeaders.set('Content-Type', 'application/json');

  const controller = new AbortController();
  const timer = timeoutMs > 0 ? setTimeout(() => controller.abort(), timeoutMs) : undefined;
  try {
    const response = await fetchImpl(url, {
      method,
      headers: requestHeaders,
      body: payload,
      credentials: withCredentials ? 'include' : 'same-origin',
      signal: controller.signal,
    });
    // The time limit covers the body too, as it did for axios.
    const text = await response.text();
    return resultOfResponseBody<T>(parseResponseText(text), response.status, expect);
  } catch (error) {
    // Ours (the time limit) or the browser's (the page going away): axios answered both as a timeout. A browser's
    // abort is a DOMException, which is not an `Error` everywhere: read its name.
    if (controller.signal.aborted || (typeof error === 'object' && error !== null && 'name' in error && error.name === 'AbortError')) {
      return requestTimeoutFailure(timeoutMs, url, method);
    }
    // `fetch` rejects with a TypeError when no response came (network, DNS, a CORS refusal).
    if (error instanceof TypeError) return requestNetworkFailure(url, method);
    return failure({
      kind: 'UNKNOWN',
      code: 'HTTP.CLIENT_ERROR',
      message: (error instanceof Error && error.message) || 'HTTP client error',
      details: { url, method },
    });
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}
